<template>
  <rest-page :fetch-endpoint="fetchEndpoints">
    <div class="dev-team-page">
      <h2 class="page-title">
        {{ $t('ApplicationDevTeam_Title_AppleDeveloperTeam') }}
      </h2>
      <p class="page-subtitle">
        {{ $t('ApplicationDevTeam_SubTitle') }}
      </p>

      <rest-form
        ref="form"
        :save-endpoint="saveEndpoint"
        :model="model"
        :rules="rules"
        @on-error="isLoading = false"
      >
        <form-section :title="$t('ApplicationDevTeam_TeamId')">
          <rest-form-item prop="team_id" class="default-width">
            <el-input
              v-model="model.team_id"
              @input="model.team_id = model.team_id.replace(/\s/g, '')"
            />
          </rest-form-item>
        </form-section>

        <form-section :title="$t('ApplicationDevTeam_KeyId')">
          <rest-form-item prop="key_id" class="default-width">
            <el-input
              v-model="model.key_id"
              @input="model.key_id = model.key_id.replace(/\s/g, '')"
            />
          </rest-form-item>
        </form-section>

        <form-section :title="$t('ApplicationDevTeam_AddP8Certificate')">
          <div>
            <el-upload
              class="dropzone"
              drag
              action="#"
              :show-file-list="false"
              :http-request="onUploadP8"
              accept=".p8"
              :disabled="isLoading"
            >
              <div class="empty-files-wrapper">
                <icon glyph="image-placeholder" class="empty-files-icon" />
                <p class="empty-files-main">
                  {{ $t('ApplicationDevTeam_Upload_Drag_Drop') }}
                  <span class="empty-files-link">
                    {{ $t('ApplicationDevTeam_Upload_Browse_Files') }}
                  </span>
                </p>
                <p class="empty-files-text">
                  {{ $t('ApplicationDevTeam_Upload_Supported_Files') }}
                </p>
              </div>
            </el-upload>
            <div v-if="isCertificateUploaded" class="upload-status">
              <i class="el-icon-success upload-status__icon" />
              <span class="upload-status__text">
                {{ $t('ApplicationDevTeam_Upload_Uploaded') }}
                <strong>
                  {{ certificateName || 'certificate.p8' }}
                </strong>
              </span>
            </div>
          </div>
        </form-section>

        <form-section
          :title="$t('ApplicationDevTeam_ChooseExistingApps')"
          :description="$t('ApplicationDevTeam_ChooseExistingApps_Hint')"
        >
          <div>
            <el-checkbox-group v-model="model.selected_applications">
              <div v-for="app in apps" :key="app.id" class="app-item">
                <el-checkbox :label="app.id">{{ app.name }}</el-checkbox>
              </div>
            </el-checkbox-group>
          </div>
        </form-section>
      </rest-form>

      <div class="footer">
        <div class="footer-ct">
          <el-button
            class="btn-connect btn-custom-icon"
            type="primary"
            @click="save"
            :loading="isLoading"
          >
            <icon class="icon" glyph="save" style="margin-right: 12px" />
            {{ $t('ApplicationList_Button_SaveDeveloper') }}
          </el-button>
        </div>
      </div>
    </div>
  </rest-page>
</template>

<script>
import RestPage from '@/common/components/rest/RestPage.vue'
import RestForm from '@/common/components/rest/RestForm.vue'
import RestFormItem from '@/common/components/rest/RestFormItem.vue'
import FormSection from '@/common/components/form/FormSection.vue'
import { projectEndpoint } from '@/core/helpers'
import { ApplicationTypes } from '@/modules/account/enum/ApplicationEnum'
import { EventBus } from '@/core/EventBus'

export default {
  name: 'ApplicationDevTeam',
  components: { RestPage, RestForm, RestFormItem, FormSection },
  data() {
    return {
      isNew: true,
      isLoading: false,
      existingTeamId: null,
      model: {
        team_id: '',
        key_id: '',
        p8_key: null,
        selected_applications: []
      },
      selectedApplications: '',
      isCertificateUploaded: false,
      certificateName: '',
      apps: []
    }
  },
  mounted() {
    EventBus.$on('handleSaveDeveloper', this.save)
  },
  destroyed() {
    EventBus.$off('handleSaveDeveloper')
  },
  computed: {
    fetchEndpoints() {
      return [
        projectEndpoint({
          method: 'get',
          url: 'live-activity-apple',
          handler: data => {
            if (Array.isArray(data) && data.length) {
              const row = data[0] || {}
              this.existingTeamId = row.team_id
              this.model.team_id = row.team_id || ''
              this.model.key_id = row.key_id || ''
              this.model.selected_applications = row.selected_applications || []
              this.isCertificateUploaded = row.is_certificate_uploaded
              this.isNew = false
            } else {
              this.isNew = true
            }
          }
        }),
        projectEndpoint({
          method: 'get',
          url: 'applications',
          params: { type: ApplicationTypes.ios },
          handler: data => {
            this.apps = (data || []).map(({ id, name }) => ({ id, name }))
          }
        })
      ]
    },
    saveEndpoint() {
      if (this.isNew) {
        return projectEndpoint({
          method: 'post',
          url: 'live-activity-apple'
        })
      } else {
        return projectEndpoint({
          method: 'patch',
          url: `live-activity-apple/${encodeURIComponent(this.existingTeamId)}`
        })
      }
    },
    rules() {
      return {
        teamId: [
          {
            required: true,
            message: this.$t('ApplicationDevTeam_TeamIdIsRequired'),
            trigger: 'blur'
          }
        ],
        keyId: [
          {
            required: true,
            message: this.$t('ApplicationDevTeam_KeyIdIsRequired'),
            trigger: 'blur'
          }
        ]
      }
    }
  },
  methods: {
    async onUploadP8(option) {
      try {
        this.isLoading = true
        const file = option && option.file
        if (!file) return
        const name = file.name || ''
        if (!/\.p8$/i.test(name)) {
          this.$message.error(
            this.$t('ApplicationDevTeam_Upload_InvalidFormat')
          )
          return
        }
        if (file.size && file.size > 500 * 1024) {
          this.$message.error(this.$t('ApplicationDevTeam_Upload_FileTooBig'))
          return
        }
        const reader = new FileReader()
        reader.readAsText(option.file)
        reader.onload = () => {
          this.model.p8_key = this.encodeToBase64(reader.result)
          this.certificateName = name
          this.isCertificateUploaded = true
        }
        this.$message.success(this.$t('ApplicationDevTeam_Upload_Uploaded'))
      } catch (e) {
        this.$message.error(e.message)
      } finally {
        this.isLoading = false
      }
    },
    encodeToBase64(content) {
      const bytes = new TextEncoder().encode(content)
      let binary = ''
      for (let i = 0; i < bytes.length; i++) {
        binary += String.fromCharCode(bytes[i])
      }
      return window.btoa(binary)
    },
    async save() {
      try {
        const data = await this.$refs['form'].save()
        if (data) {
          this.isNew = false
          this.existingTeamId =
            (data && data.team_id) || this.model.team_id || this.existingTeamId
        }
      } catch (e) {
        this.$message.error(e.message)
      }
    }
  }
}
</script>

<style scoped>
.dev-team-page {
  padding: 0 115px 38px 115px;
}

.page-title {
  margin: 0 0 6px 0;
}

.page-subtitle {
  color: #6b7280;
  margin-bottom: 18px;
}

.default-width {
  width: 553px;
}

.dropzone {
  border: 1px solid #dbdee7;
  border-radius: 4px;
  text-align: center;
  cursor: pointer;
  margin-top: 4px;
  background-color: #f8fafc;
  max-height: 400px;
  overflow: auto;
}
/deep/ .el-upload {
  width: 100%;
}
/deep/ .el-upload-dragger {
  width: 100%;
  background: transparent;
  border: none;
  padding: 15px;
  height: unset;
}
.empty-files-wrapper {
  display: flex;
  flex-direction: column;
  gap: 7px;
  align-items: center;
}
.empty-files-icon {
  font-size: 18px;
}
.empty-files-main {
  font-family: 'Open Sans', sans-serif;
  font-size: 14px;
  line-height: 22px;
  color: #4b515f;
}
.empty-files-link {
  color: #1d87c9;
  cursor: pointer;
  text-decoration: underline;
}
.upload-status {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  margin-top: 10px;
  background: #f6ffed;
  border: 1px solid #b7eb8f;
  border-radius: 4px;
}
.upload-status__icon {
  font-size: 18px;
  color: #52c41a;
}
.upload-status__text strong {
  margin-left: 4px;
}
.upload-status__clear {
  margin-left: 6px;
}
.empty-files-text {
  font-family: 'Open Sans', sans-serif;
  font-size: 13px;
  line-height: 13px;
  color: #5c6374;
}

/deep/ .el-checkbox__input.is-checked .el-checkbox__inner {
  border-color: #1d87c8 !important;
  background-color: #1d87c8;
}
/deep/ .el-checkbox.is-bordered.is-checked {
  border-color: #1d87c8;
  background-color: #e6f3fb;
}
/deep/ .el-checkbox__inner {
  border-color: #dcdfe6 !important;
  background-color: #fff;
  width: 20px;
  height: 20px;
  border-radius: 4px;
}
/deep/ .el-checkbox__inner::after {
  height: 8px;
  border: 2px solid #fff;
  border-left: 0;
  border-top: 0;
  top: 3px;
  left: 6px;
  width: 4px;
}
/deep/ .el-checkbox__label {
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  color: #4b515f;
}

/deep/ .el-checkbox__input.is-checked + .el-checkbox__label {
  color: #4b515f;
}

.app-item {
  margin: 8px 0;
}

.footer {
  position: fixed;
  bottom: 0;
  right: 0;
  left: 0;
  width: 100%;
  height: 60px;
  background: #ffffff;
  z-index: 1;
  border-top: 1px solid #e5e5e5;
}

.footer-ct {
  margin-right: 20px;
  margin-bottom: 12px;
  position: absolute;
  bottom: 0;
  right: 0;
}
</style>



// WEBPACK FOOTER //
// src/modules/account/pages/ApplicationDevTeam.vue